/**
 * Loan-program translation edge.
 * -----------------------------
 * The application reasons about loan products using the canonical codes in
 * LOAN_PROGRAM_CODES (PROGRAM_001..PROGRAM_013). Encompass does not: each
 * company's instance stores its own brand name in CX.LOAN.PROD.BROKER.SELECT
 * and CX.LOAN.PRODUCT.NAME - "ACTIVATOR" for ChampsTPO, "SUNNY" for SkyTPO -
 * and some products exist in only one instance.
 *
 * This module is the only place those two vocabularies meet. It binds the
 * pure lookups in src/config/organizations.js to the org resolved from
 * REACT_APP_ORGANIZATION, and loanApi's field reader/writer call it so that
 * values are canonical everywhere between them:
 *
 *   Encompass -> fieldReader -> [canonical codes] -> fieldWriter -> Encompass
 *
 * Display is the other edge: getLoanProgramLabel() turns a code back into the
 * brand name for anything the user reads.
 */

import {
    LOAN_PROGRAM_CODES,
    getCanonicalLoanProgram,
    getLoanProgramOptions,
    getLoanProgramValue,
    isLoanProgramDisabled,
    isLoanProgramNameDisabled
} from '../config/organizations';
import { getOrgConfig } from './organizationConfigUtil';

/**
 * Encompass fields whose value is a loan-program name. These are translated
 * automatically on read and write; anything else passes through untouched.
 */
export const LOAN_PROGRAM_FIELD_IDS = [
    'CX.LOAN.PROD.BROKER.SELECT',
    'CX.LOAN.PRODUCT.NAME'
];

const PROGRAM_FIELD_ID_SET = new Set(
    LOAN_PROGRAM_FIELD_IDS.map((id) => id.toUpperCase())
);

/**
 * True when a field id carries a loan-program name.
 *
 * Encompass suffixes repeated fields ("CX.LOAN.PRODUCT.NAME#2", "...P2"), so
 * match on the base id rather than the whole string.
 */
export const isLoanProgramField = (fieldId) => {
    const base = String(fieldId || '')
        .split('#')[0]
        .replace(/\.P\d+$/i, '')
        .trim()
        .toUpperCase();

    return PROGRAM_FIELD_ID_SET.has(base);
};

const isCanonicalCode = (value) =>
    Object.prototype.hasOwnProperty.call(
        LOAN_PROGRAM_CODES,
        String(value || '').trim().toUpperCase()
    );

/**
 * This org's Encompass value -> canonical code. Already-canonical input is
 * returned unchanged, so this is safe to apply more than once.
 */
export const toCanonicalLoanProgram = (encompassValue) => {
    if (encompassValue == null || encompassValue === '') return '';
    if (isCanonicalCode(encompassValue)) return String(encompassValue).trim().toUpperCase();

    return getCanonicalLoanProgram(getOrgConfig(), encompassValue);
};

/**
 * Canonical code -> this org's Encompass value.
 *
 * A value that is not a canonical code is passed through as-is: it was
 * written by something outside this mapping, and blanking it on the way back
 * to Encompass would destroy loan data. A code the org does not carry
 * resolves to "" - that product genuinely has no value in this instance.
 */
export const toEncompassLoanProgram = (canonicalCode) => {
    if (canonicalCode == null || canonicalCode === '') return '';
    if (!isCanonicalCode(canonicalCode)) return String(canonicalCode);

    return getLoanProgramValue(getOrgConfig(), String(canonicalCode).trim().toUpperCase()) || '';
};

/**
 * The brand name to show the user for a canonical code. Same string that is
 * stored in Encompass; "" when this org does not carry the product.
 */
export const getLoanProgramLabel = (canonicalCode) => toEncompassLoanProgram(canonicalCode);

/**
 * True when this org lists the product but brokers may not select it (the
 * org's `disabledLoanPrograms`). Accepts a canonical code or Encompass value.
 */
export const isLoanProgramDisabledForOrg = (value) =>
    isLoanProgramDisabled(getOrgConfig(), toCanonicalLoanProgram(value));

/**
 * The program that blocks Send/Request Disclosures and Submit Loan for this
 * org, or "" when nothing does. Checks the broker-selected product
 * (CX.LOAN.PROD.BROKER.SELECT) against `disabledLoanPrograms`, then the 1401
 * Loan Program against `disabledLoanProgramNames`. The returned name is shown
 * in the "Loan Program Not Available" note.
 */
export const getBlockedLoanProgramName = (productValue, loanProgramName) => {
    if (isLoanProgramDisabledForOrg(productValue)) {
        return getLoanProgramLabel(toCanonicalLoanProgram(productValue));
    }

    if (isLoanProgramNameDisabled(getOrgConfig(), loanProgramName)) {
        return (loanProgramName || '').toString().trim();
    }

    return '';
};

/** True when this org carries the product at all. */
export const isLoanProgramAvailable = (canonicalCode) =>
    Boolean(getLoanProgramValue(getOrgConfig(), canonicalCode));

/**
 * The product name in title case, for prose and headings.
 *
 * The org config stores names as Encompass holds them - upper case, e.g.
 * "SUPER JUMBO" - which shouts in a sentence. This gives "Super Jumbo" /
 * "Starry" instead. Returns "" when the org does not carry the product.
 */
export const getLoanProgramTitle = (canonicalCode) =>
    getLoanProgramLabel(canonicalCode)
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Every product this org carries, in PROGRAM_001..012 order, as
 * `{ code, value, text }`. Use getLoanProgramOptionsFor() when the screen
 * defines its own subset and ordering.
 */
export const getAllLoanProgramOptions = () => getLoanProgramOptions(getOrgConfig());

/**
 * Turn a list of canonical codes - typically an `options` array out of
 * src/data/** - into dropdown options carrying this org's brand names.
 *
 * Codes the org does not carry are dropped, which is how a product like ALLY
 * disappears from SkyTPO's dropdowns without any screen knowing about it.
 * `value` is the canonical code, so component state and every comparison stay
 * canonical; only `text` is branded.
 */
export const getLoanProgramOptionsFor = (canonicalCodes = []) =>
    (canonicalCodes || [])
        .map((entry) => (typeof entry === 'string' ? entry : entry?.value))
        .map((code) => ({ code, label: getLoanProgramLabel(code) }))
        .filter(({ label }) => Boolean(label))
        .map(({ code, label }) => ({
            value: code,
            text: label,
            disabled: isLoanProgramDisabled(getOrgConfig(), code)
        }));

/**
 * Render-ready options for a field definition out of src/data/**.
 *
 * For loan-program fields the stored `options` are canonical codes, so the
 * brand name has to be resolved per org and unavailable products dropped.
 * Every other field is normalized to `{ value, text }` and left alone.
 *
 * Renderers should call this instead of reading `field.options` directly - a
 * screen that skips it displays the raw code (PROGRAM_002), which is wrong in
 * an obvious way rather than showing one company's brand name to the other's
 * users.
 */
export const resolveFieldOptions = (fieldDef) => {
    const rawOptions = fieldDef?.options || [];

    const isProgramField =
        fieldDef?.valueSet === 'loanProgram' || isLoanProgramField(fieldDef?.id);

    if (isProgramField) return getLoanProgramOptionsFor(rawOptions);

    return rawOptions.map((option) =>
        typeof option === 'string'
            ? { value: option, text: option }
            : { ...option, value: String(option?.value ?? ''), text: option?.text ?? option?.value }
    );
};

/**
 * Compare a program value against one or more canonical codes, tolerating
 * either vocabulary on the left. Prefer this over `===` when the value's
 * provenance is not obvious.
 */
export const isLoanProgram = (value, ...canonicalCodes) => {
    const code = toCanonicalLoanProgram(value);
    return canonicalCodes.flat().some((expected) => code === expected);
};

/**
 * Canonicalize a fieldId -> value map coming back from Encompass.
 * Non-program fields are copied through untouched.
 */
export const canonicalizeFieldValueMap = (fieldValues) => {
    if (!fieldValues || typeof fieldValues !== 'object') return fieldValues;

    const out = Array.isArray(fieldValues) ? [...fieldValues] : { ...fieldValues };

    Object.keys(out).forEach((fieldId) => {
        if (!isLoanProgramField(fieldId)) return;
        out[fieldId] = toCanonicalLoanProgram(out[fieldId]);
    });

    return out;
};

/**
 * Localize a `[{ id, value }]` write payload on its way to Encompass, so the
 * instance receives its own brand name rather than a canonical code.
 */
export const localizeFieldWritePayload = (fields) => {
    if (!Array.isArray(fields)) return fields;

    return fields.map((field) => {
        if (!field || !isLoanProgramField(field.id ?? field.fieldName)) return field;

        return { ...field, value: toEncompassLoanProgram(field.value) };
    });
};
